import React, { useState, useEffect } from "react";
import { X, UserCheck, Search, ChevronDown } from "lucide-react";
import { employeesAPI } from "../../services/api";

const AssignModal = ({ onClose, selectedCount, onConfirmAssign }) => {
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await employeesAPI.getAllEmployees();
        const data = Array.isArray(response.data) ? response.data : response.data || [];
        const mapped = data
          .filter(u => Array.isArray(u.roles) && u.roles.some(r => r === 'CRM' || r === 'ROLE_CRM'))
          .map(u => ({
            id: u.id,
            name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Unknown'
          }))
          .filter(u => u.name && u.name !== 'Unknown');
        setUsers(mapped);
      } catch (err) {
        console.error('Failed to fetch employees:', err);
      } finally {
        setLoadingUsers(false);
      }
    };
    fetchUsers();
  }, []);

  const filteredUsers = users.filter(u =>
    u.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const selectedUser = users.find(u => u.id === selectedUserId);

  const handleUpdate = () => {
    if (!selectedUserId) {
      alert("Please select a user to assign ❌");
      return;
    }
    onConfirmAssign(selectedUserId);
    onClose();
  };

  const handleCancel = () => {
    setSelectedUserId('');
    setSearchQuery('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-visible">
        {/* Top accent bar */}
        <div className="h-1.5 w-full bg-gradient-to-r from-purple-400 via-purple-500 to-indigo-500 rounded-t-2xl" />

        {/* Header */}
        <div className="flex items-start justify-between px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-purple-50 flex items-center justify-center border border-purple-100">
              <UserCheck size={22} className="text-purple-500" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                Assign {selectedCount > 1 ? 'Records' : 'Record'}
              </h2>
              <p className="text-xs text-gray-400 mt-0.5">
                {selectedCount} {selectedCount === 1 ? 'record' : 'records'} selected
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Contact Owner
          </label>

          {loadingUsers ? (
            <div className="flex items-center justify-center h-12 rounded-xl bg-gray-50 border border-gray-200">
              <div className="w-4 h-4 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="relative">
              {/* Dropdown trigger */}
              <button
                type="button"
                onClick={() => setDropdownOpen(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm text-left hover:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all"
              >
                <span className={selectedUser ? 'text-gray-900 font-medium' : 'text-gray-400'}>
                  {selectedUser ? selectedUser.name : 'Select a team member...'}
                </span>
                <ChevronDown
                  size={16}
                  className={`text-gray-400 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown panel */}
              {dropdownOpen && (
                <div className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                  {/* Search */}
                  <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-100 sticky top-0 bg-white z-10">
                    <Search size={14} className="text-gray-400 flex-shrink-0" />
                    <input
                      type="text"
                      placeholder="Search members..."
                      value={searchQuery}
                      onChange={e => setSearchQuery(e.target.value)}
                      className="flex-1 text-sm outline-none placeholder:text-gray-400"
                      autoFocus
                    />
                    {searchQuery && (
                      <button onClick={() => setSearchQuery('')} className="text-gray-400 hover:text-gray-600">
                        <X size={12} />
                      </button>
                    )}
                  </div>

                  {/* Scrollable list — fixed height forces scroll */}
                  <ul
                    style={{ maxHeight: '400px', overflowY: 'auto' }}
                    className="py-1"
                  >
                    {filteredUsers.length === 0 ? (
                      <li className="px-4 py-3 text-sm text-gray-400 text-center">
                        No members found
                      </li>
                    ) : (
                      filteredUsers.map(user => (
                        <li key={user.id}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedUserId(user.id);
                              setDropdownOpen(false);
                              setSearchQuery('');
                            }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left hover:bg-purple-50 transition-colors ${
                              selectedUserId === user.id
                                ? 'bg-purple-50 text-purple-700 font-medium'
                                : 'text-gray-700'
                            }`}
                          >
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                              {user.name.charAt(0).toUpperCase()}
                            </div>
                            {user.name}
                          </button>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Selected user preview */}
          {selectedUser && (
            <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-purple-50 border border-purple-100 rounded-lg">
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-purple-400 to-indigo-400 flex items-center justify-center text-white text-xs font-bold">
                {selectedUser.name.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm text-purple-700 font-medium">{selectedUser.name}</span>
              <span className="text-xs text-purple-400 ml-auto">will be assigned</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 bg-gray-50 border-t border-gray-100 rounded-b-2xl">
          <button
            onClick={handleCancel}
            className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50 hover:border-gray-300 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleUpdate}
            disabled={!selectedUserId}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-purple-500 rounded-xl hover:bg-purple-600 active:scale-95 transition-all shadow-sm shadow-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <UserCheck size={15} />
            Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignModal;